import { useState, useEffect, useCallback } from 'react'
import { api } from '../../../services/api'
import { useRateLimit } from '../../../hooks/useRateLimit'
import { RateLimitIndicator } from '../../../components/RateLimitIndicator'
import {
  Container,
  Card,
  InputDiv,
  Result,
  MathDiv,
  Select,
  ActionButtonsRow,
  DesktopOnlyDiv,
  MobileOnlyDiv,
  CopyHTMLButton,
  PageContainer,
  PageHeader,
  Badge,
  InfoCard,
  BackButton,
} from '../../../styles/global'
import { BlockMath } from 'react-katex'
import 'katex/dist/katex.min.css'
import { toast } from 'react-toastify'
import { ValidatedInput } from '../../../components/ValidatedInput'
import { Spinner } from '../../../components/Spinner'
import { HistoryNavigation } from '../../../components/HistoryNavigation'
import { LoadingOverlay } from '../../../components/LoadingOverlay'
import { FiArrowLeft, FiInfo } from 'react-icons/fi'

import { copyResultsToClipboard } from '../../../utils/copyResultsToClipboard'
import { roundToDecimals } from '../../../utils/numberFormat'
export function ConcreteTorsion() {
  const { canCalculate, updateFromHeaders, handleRateLimitError, fetchRateLimitStatus } = useRateLimit()

  const [fck, setFck] = useState(20)
  const [fyk, setFyk] = useState(500)
  const [b, setB] = useState('')
  const [h, setH] = useState('')
  const [dl, setDl] = useState('')
  const [tk, setTk] = useState('')
  const [vk, setVk] = useState('')
  const [gammaf, setGammaf] = useState(1.4)
  const [result, setResult] = useState('')
  const [isFormValid, setIsFormValid] = useState(false)
  const [data, setData] = useState({})
  const [selectedRebar, setSelectedRebar] = useState('Ø5.0')
  const [selectedStirrup, setSelectedStirrup] = useState('Ø5.0')
  const [touched, setTouched] = useState({})
  const [errors, setErrors] = useState({})
  const [isLoading, setIsLoading] = useState(false)
  const [isRecalculating, setIsRecalculating] = useState(false)
  const [history, setHistory] = useState([])
  const [currentHistoryIndex, setCurrentHistoryIndex] = useState(-1)

  const HISTORY_KEY = 'concrete-torsion-history'
  const MAX_HISTORY = 10

  useEffect(() => {
    const savedHistory = localStorage.getItem(HISTORY_KEY)
    if (savedHistory) {
      try {
        const parsed = JSON.parse(savedHistory)
        setHistory(parsed)
      } catch (error) {
        console.error('Erro ao carregar histórico:', error)
      }
    }
  }, [])

  const saveToHistory = useCallback((calculationData) => {
    const newEntry = {
      ...calculationData,
      timestamp: new Date().toISOString(),
      id: Date.now(),
    }

    setHistory((prevHistory) => {
      const newHistory = [newEntry, ...prevHistory].slice(0, MAX_HISTORY)
      localStorage.setItem(HISTORY_KEY, JSON.stringify(newHistory))
      return newHistory
    })
    setCurrentHistoryIndex(0)
  }, [])

  const loadFromHistory = useCallback(
    (index) => {
      if (index < 0 || index >= history.length) return

      const entry = history[index]
      setFck(entry.fck)
      setFyk(entry.fyk)
      setB(entry.b)
      setH(entry.h)
      setDl(entry.dl)
      setTk(entry.tk)
      setVk(entry.vk)
      setGammaf(entry.gammaf || 1.4)
      setData(entry.data)
      setSelectedRebar(entry.selectedRebar)
      setSelectedStirrup(entry.selectedStirrup)
      setResult(entry.result)
      setCurrentHistoryIndex(index)
    },
    [history]
  )

  const navigateHistory = useCallback(
    (direction) => {
      const newIndex = currentHistoryIndex + direction
      if (newIndex >= 0 && newIndex < history.length) {
        loadFromHistory(newIndex)
      }
    },
    [currentHistoryIndex, history.length, loadFromHistory]
  )

  const validateField = useCallback((name, value) => {
    if (value === '' || value === null || value === undefined) {
      return 'Campo obrigatório'
    }

    const numValue = Number(value)

    if (isNaN(numValue)) {
      return 'Valor deve ser um número'
    }

    if (numValue <= 0) {
      return 'Valor deve ser maior que 0'
    }

    return null
  }, [])

  useEffect(() => {
    const newErrors = {}

    newErrors.fck = validateField('fck', fck)
    newErrors.fyk = validateField('fyk', fyk)
    newErrors.b = validateField('b', b)
    newErrors.h = validateField('h', h)
    newErrors.dl = validateField('dl', dl)
    newErrors.tk = validateField('tk', tk)
    newErrors.vk = validateField('vk', vk)
    newErrors.gammaf = validateField('gammaf', gammaf)

    setErrors(newErrors)

    const hasErrors = Object.values(newErrors).some((error) => error !== null)
    setIsFormValid(!hasErrors)
  }, [fck, fyk, b, h, dl, tk, vk, gammaf, validateField])

  const handleBlur = (fieldName) => {
    setTouched((prev) => ({ ...prev, [fieldName]: true }))
  }

  async function handleCalculateBeam(event) {
    event.preventDefault()

    const hasExistingResults = result !== ''

    setIsLoading(true)
    if (hasExistingResults) {
      setIsRecalculating(true)
    }

    try {
      const data = {
        fck,
        fyk,
        b,
        h,
        dl,
        td: tk * gammaf,
        vd: vk * gammaf,
      }

      setData(data)

      const response = await api.post('/concrete/torsion', data)
      setResult(response.data)

      if (response.rateLimit) {
        updateFromHeaders({
          'x-ratelimit-limit': response.rateLimit.limit.toString(),
          'x-ratelimit-remaining': response.rateLimit.remaining.toString(),
          'x-ratelimit-reset': response.rateLimit.reset,
        })
      }

      await fetchRateLimitStatus()

      saveToHistory({
        fck,
        fyk,
        b,
        h,
        dl,
        tk,
        vk,
        gammaf,
        data,
        selectedRebar,
        selectedStirrup,
        result: response.data,
      })

      toast.success('Cálculo realizado com sucesso!')
    } catch (error) {
      console.error('Erro ao calcular:', error)

      if (handleRateLimitError(error)) {
        toast.error(error.response?.data?.message || 'Você atingiu o limite de cálculos diários.')
        return
      }

      toast.error('Erro ao realizar o cálculo. Tente novamente.')
    } finally {
      setIsLoading(false)
      if (hasExistingResults) {
        setTimeout(() => setIsRecalculating(false), 500)
      }
    }
  }

  function handleSave() {
    const stateData = {
      fck,
      fyk,
      b,
      h,
      dl,
      tk,
      vk,
      gammaf,
      isFormValid,
      data,
      selectedRebar,
      selectedStirrup,
      result,
    }
    const jsonString = JSON.stringify(stateData, null, 2)
    const blob = new Blob([jsonString], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'Concreto Torção.json'
    link.click()
    URL.revokeObjectURL(url)
    toast.success('Estado salvo com sucesso!')
  }

  function handleFileUpload(event) {
    const file = event.target.files[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = (e) => {
      try {
        const jsonData = JSON.parse(e.target.result)

        setFck(jsonData.fck)
        setFyk(jsonData.fyk)
        setB(jsonData.b)
        setH(jsonData.h)
        setDl(jsonData.dl)
        setTk(jsonData.tk)
        setVk(jsonData.vk)
        setGammaf(jsonData.gammaf || 1.4)
        setIsFormValid(jsonData.isFormValid)
        setData(jsonData.data)
        setSelectedRebar(jsonData.selectedRebar)
        setSelectedStirrup(jsonData.selectedStirrup)
        setResult(jsonData.result)
        toast.success('Estado carregado com sucesso!')
      } catch (error) {
        console.error('Erro ao ler o arquivo JSON:', error)
        toast.error('Erro ao carregar o estado')
      }
    }
    reader.readAsText(file)
  }

  function handleCopyResults() {
    copyResultsToClipboard({
      result,
    })
  }

  return (
    <PageContainer>
      <PageHeader $gradient="linear-gradient(135deg, #667eea 0%, #764ba2 100%)">
        <div className="header-content">
          <BackButton to="/concreto">
            <FiArrowLeft /> Voltar para Concreto
          </BackButton>
          <div className="breadcrumb">
            <a href="/">Home</a>
            <span>/</span>
            <a href="/concreto">Concreto</a>
            <span>/</span>
            <span>Torção</span>
          </div>
          <h1>Torção em Concreto Armado</h1>
          <p className="subtitle">Dimensionamento de armaduras submetidas à combinação de torção e cisalhamento</p>
          <div className="badges">
            <Badge>Popular</Badge>
            <Badge>NBR 6118:2014</Badge>
            <Badge>Torção + Cisalhamento</Badge>
          </div>
        </div>
      </PageHeader>

      <InfoCard
        $background="linear-gradient(135deg, #f3f0ff 0%, #e9e0ff 100%)"
        $border="2px solid #d4c5f9"
        $primaryColor="#764ba2"
      >
        <div className="info-content">
          <div className="icon">
            <FiInfo />
          </div>
          <div className="text">
            <h3>Sobre esta Calculadora</h3>
            <p>
              Dimensiona aços transversal e longitudinal para elementos de concreto armado sujeitos à torção, seguindo
              os requisitos da NBR 6118:2014.
            </p>
            <p>
              Avalia a seção equivalente, as verificações de segurança e sugere bitolas compatibilizadas com o cortante.
            </p>
            <div className="norms">
              <span>NBR 6118:2014</span>
              <span>Torção + Cisalhamento</span>
            </div>
          </div>
        </div>
      </InfoCard>

      <Container>
        <Card>
          <RateLimitIndicator />

          <h2>Seção à torção</h2>
          {history.length > 0 && (
            <HistoryNavigation
              currentIndex={currentHistoryIndex}
              totalItems={history.length}
              onNavigate={navigateHistory}
              currentTimestamp={currentHistoryIndex >= 0 ? history[currentHistoryIndex]?.timestamp : null}
            />
          )}
          <form onSubmit={handleCalculateBeam}>
            <h3>Materiais</h3>
            <InputDiv>
              <span>Resistência Característica do Concreto (MPa)</span>
              <ValidatedInput
                name="fck"
                type="number"
                value={fck || ''}
                onChange={(event) => setFck(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>Resistência Característica do Aço (MPa)</span>
              <ValidatedInput
                name="fyk"
                type="number"
                value={fyk || ''}
                onChange={(event) => setFyk(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <h3>Geometria</h3>
            <InputDiv>
              <span>Largura (cm)</span>
              <ValidatedInput
                name="b"
                type="number"
                value={b || ''}
                onChange={(event) => setB(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>Altura (cm)</span>
              <ValidatedInput
                name="h"
                type="number"
                value={h || ''}
                onChange={(event) => setH(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>Distância da barra tracionada à face tracionada (cm)</span>
              <ValidatedInput
                name="dl"
                type="number"
                value={dl || ''}
                onChange={(event) => setDl(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <h3>Esforços</h3>
            <InputDiv>
              <span>Torção Característica (kN.cm)</span>
              <ValidatedInput
                name="tk"
                type="number"
                value={tk || ''}
                onChange={(event) => setTk(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>Cortante Característico (kN)</span>
              <ValidatedInput
                name="vk"
                type="number"
                value={vk || ''}
                onChange={(event) => setVk(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>Coeficiente de ponderação γf</span>
              <ValidatedInput
                name="gammaf"
                type="number"
                value={gammaf || ''}
                onChange={(event) => setGammaf(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <ActionButtonsRow>
              <button type="button" onClick={handleSave}>
                Salvar
              </button>
              <button type="button" onClick={() => document.getElementById('file-input').click()}>
                Carregar
              </button>
              <button type="submit" disabled={!isFormValid || isLoading || !canCalculate}>
                {isLoading ? (
                  <>
                    <Spinner size="16px" />
                    Calculando...
                  </>
                ) : !canCalculate ? (
                  'Limite atingido'
                ) : (
                  'Calcular'
                )}
              </button>
            </ActionButtonsRow>
            <input type="file" id="file-input" onChange={handleFileUpload} style={{ display: 'none' }} />
          </form>
        </Card>
        {result !== '' && (
          <>
            <Result $isRecalculating={isRecalculating}>
              {isRecalculating && <LoadingOverlay message="Recalculando..." />}
              <h2>Resultados</h2>
              <h3>Verificação da biela</h3>
              <MathDiv>
                {result.ratio <= 1 ? (
                  <BlockMath
                    math={`\\dfrac{${data.vd}}{${result.vrd2}}+\\dfrac{${data.td}}{${result.trd2}}=${roundToDecimals(result.ratio, 2)}\\leq 1`}
                  />
                ) : (
                  <div style={{ color: 'red', display: 'flex', flexDirection: 'column' }}>
                    <BlockMath
                      math={`\\dfrac{${data.vd}}{${result.vrd2}}+\\dfrac{${data.td}}{${result.trd2}}=${roundToDecimals(result.ratio, 2)} > 1`}
                    />
                    <p>Tensão na biela inadequada.</p>
                  </div>
                )}
              </MathDiv>
              <h3>Armadura transversal</h3>
              <MathDiv>
                <InputDiv>
                  <p>Seleção da bitola</p>
                  <Select
                    id="stirrupSelect"
                    value={selectedStirrup}
                    onChange={(event) => setSelectedStirrup(event.target.value)}
                    style={{ marginTop: '10px' }}
                  >
                    {result.aswRebars.map((rebar, index) => (
                      <option key={index} value={rebar.name}>
                        {rebar.name}
                      </option>
                    ))}
                  </Select>
                </InputDiv>
                <p>
                  Serão necessários estribos de {selectedStirrup}c/
                  {result.aswRebars.find((r) => r.name == selectedStirrup).spacing}.
                </p>
                <p style={{ color: 'red' }}>Não verificada a armadura de cisalhamento.</p>
              </MathDiv>
              <h3>Armadura longitudinal</h3>
              <MathDiv>
                <InputDiv>
                  <p>Seleção da bitola</p>
                  <Select
                    id="rebarSelect"
                    value={selectedRebar}
                    onChange={(event) => setSelectedRebar(event.target.value)}
                    style={{ marginTop: '10px' }}
                  >
                    {result.aslRebars.map((rebar, index) => (
                      <option key={index} value={rebar.name}>
                        {rebar.name}
                      </option>
                    ))}
                  </Select>
                </InputDiv>
                <p>
                  Serão necessários {result.aslRebars.find((r) => r.name == selectedRebar).quantity} barras de{' '}
                  {selectedRebar} em cada uma das faces.
                </p>
                <p style={{ color: 'red' }}>Não verificada a armadura de flexão.</p>
              </MathDiv>
            </Result>
            <Result className="results-section" $isRecalculating={isRecalculating}>
              {isRecalculating && <LoadingOverlay />}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                <h2>Cálculos</h2>
                <CopyHTMLButton type="button" onClick={handleCopyResults}>
                  📋 Copiar Cálculos
                </CopyHTMLButton>
              </div>
              <h3>Seção equivalente</h3>
              <MathDiv>
                <BlockMath math={`A=b \\cdot h`} />
                <BlockMath
                  math={`A=${data.b} \\cdot ${data.h}=${result.equivalentSectionResponse.area} \\, \\text{cm}^2`}
                />
                <BlockMath math={`u=2 \\cdot (b+h)`} />
                <BlockMath
                  math={`u=2 \\cdot (${data.b}+${data.h})=${result.equivalentSectionResponse.perimeter} \\, \\text{cm}`}
                />
                <BlockMath
                  math={`\\dfrac{A}{u}=\\dfrac{${result.equivalentSectionResponse.area}}{${result.equivalentSectionResponse.perimeter}}=${roundToDecimals(result.equivalentSectionResponse.ratio, 2)} \\, \\text{cm}`}
                />
                {result.equivalentSectionResponse.case == 1 && (
                  <>
                    <BlockMath math={`\\dfrac{A}{u} \\geq 2 \\cdot C_1=2 \\cdot ${data.dl}=${data.dl * 2}`} />
                    <BlockMath
                      math={`\\boxed{h_e=\\dfrac{A}{u}=${result.equivalentSectionResponse.he} \\, \\text{cm}}`}
                    />
                    <BlockMath math={`A_e=(b-h_e) \\cdot (h-h_e)`} />
                    <BlockMath
                      math={`A_e=(${data.b}-${result.equivalentSectionResponse.he}) \\cdot (${data.h}-${result.equivalentSectionResponse.he})`}
                    />
                    <BlockMath math={`\\boxed{A_e=${result.equivalentSectionResponse.areae} \\, \\text{cm}^2}`} />
                    <BlockMath math={`u_e=2 \\cdot (b+h-2\\cdot h_e)`} />
                    <BlockMath
                      math={`u_e=2 \\cdot (${data.b}+${data.h}-2\\cdot ${result.equivalentSectionResponse.he})`}
                    />
                    <BlockMath math={`\\boxed{u_e=${result.equivalentSectionResponse.ue} \\, \\text{cm}}`} />
                  </>
                )}
                {result.equivalentSectionResponse.case == 2 && (
                  <>
                    <BlockMath math={`\\dfrac{A}{u} < 2 \\cdot C_1=2 \\cdot ${data.dl}=${data.dl * 2}`} />
                    <BlockMath math={`h_e=\\dfrac{A}{u} \\leq b - 2\\cdot C_1`} />
                    <BlockMath
                      math={`\\boxed{h_e=\\dfrac{A}{u} \\leq ${data.b} - 2\\cdot ${data.dl}=${result.equivalentSectionResponse.he} \\, \\text{cm}}`}
                    />
                    <BlockMath math={`A_e=(b-2 \\cdot C_1) \\cdot (h-2 \\cdot C_1)`} />
                    <BlockMath math={`A_e=(${data.b}-2 \\cdot ${data.dl}) \\cdot (${data.h}-2 \\cdot ${data.dl})`} />
                    <BlockMath math={`\\boxed{A_e=${result.equivalentSectionResponse.areae} \\, \\text{cm}^2}`} />
                    <BlockMath math={`u_e=2 \\cdot (b+h-4\\cdot C_1)`} />
                    <BlockMath math={`u_e=2 \\cdot (${data.b}+${data.h}-4\\cdot ${data.dl})`} />
                    <BlockMath math={`\\boxed{u_e=${result.equivalentSectionResponse.ue} \\, \\text{cm}}`} />
                  </>
                )}
              </MathDiv>
              <h3>Verificação da biela</h3>
              <MathDiv>
                <h4>Cisalhamento</h4>
                <DesktopOnlyDiv>
                  <BlockMath
                    math={`V_{Rd2}=0,27 \\cdot \\left(1 - \\dfrac{f_{ck}}{250} \\right) \\cdot \\dfrac{f_{ck}}{\\gamma_c} \\cdot b \\cdot d`}
                  />
                  <BlockMath
                    math={`V_{Rd2}=0,27 \\cdot \\left(1 - \\dfrac{${data.fck} }{250} \\right) \\cdot \\dfrac{${data.fck / 10} }{1,4} \\cdot ${data.b} \\cdot ${data.h - data.dl}`}
                  />
                </DesktopOnlyDiv>
                <MobileOnlyDiv>
                  <BlockMath math={`V_{Rd2}=A \\cdot B`} />
                  <BlockMath math={`A=0,27 \\cdot \\left(1 - \\dfrac{f_{ck}}{250} \\right)`} />
                  <BlockMath math={`A=0,27 \\cdot \\left(1 - \\dfrac{${data.fck} }{250} \\right)`} />
                  <BlockMath math={`B=\\dfrac{f_{ck}}{\\gamma_c} \\cdot b \\cdot d`} />
                  <BlockMath math={`B=\\dfrac{${data.fck / 10} }{1,4} \\cdot ${data.b} \\cdot ${data.h - data.dl}`} />
                </MobileOnlyDiv>
                <BlockMath math={`\\boxed{V_{Rd2}=${result.vrd2} \\, \\text{kN}}`} />
                <h4>Torção</h4>
                <DesktopOnlyDiv>
                  <BlockMath
                    math={`T_{Rd2}=0,5 \\cdot \\left(1 - \\dfrac{f_{ck}}{250} \\right) \\cdot \\dfrac{f_{ck}}{\\gamma_c} \\cdot A_e \\cdot h_e`}
                  />
                  <BlockMath
                    math={`T_{Rd2}=0,5 \\cdot \\left(1 - \\dfrac{${data.fck}}{250} \\right) \\cdot \\dfrac{${data.fck / 10}}{1,4} \\cdot ${result.equivalentSectionResponse.areae} \\cdot ${result.equivalentSectionResponse.he}`}
                  />
                </DesktopOnlyDiv>
                <MobileOnlyDiv>
                  <BlockMath math={`T_{Rd2}=C \\cdot D`} />
                  <BlockMath math={`C=0,5 \\cdot \\left(1 - \\dfrac{f_{ck}}{250} \\right)`} />
                  <BlockMath math={`D=\\dfrac{f_{ck}}{\\gamma_c} \\cdot A_e \\cdot h_e`} />
                </MobileOnlyDiv>
                <BlockMath math={`\\boxed{T_{Rd2}=${result.trd2} \\, \\text{kN}\\cdot\\text{m}}`} />
                <h4>Verificação</h4>
                <BlockMath math={`\\dfrac{V_{Sd}}{V_{Rd2}}+\\dfrac{T_{Sd}}{T_{Rd2}}`} />
                {result.ratio <= 1 ? (
                  <BlockMath
                    math={`\\boxed{\\dfrac{${data.vd}}{${result.vrd2}}+\\dfrac{${data.td}}{${result.trd2}}=${roundToDecimals(result.ratio, 2)}\\leq 1}`}
                  />
                ) : (
                  <div style={{ color: 'red', display: 'flex', flexDirection: 'column' }}>
                    <BlockMath
                      math={`\\boxed{\\dfrac{${data.vd}}{${result.vrd2}}+\\dfrac{${data.td}}{${result.trd2}}=${roundToDecimals(result.ratio, 2)} > 1}`}
                    />
                    <p>Tensão na biela inadequada.</p>
                  </div>
                )}
              </MathDiv>
              <h3>Armadura transversal</h3>
              <MathDiv>
                <BlockMath math={`A_{sw}=\\dfrac{100 \\cdot T_{Sd}}{2 \\cdot A_e \\cdot f_{yd}}`} />
                <BlockMath
                  math={`A_{sw}=\\dfrac{100 \\cdot ${data.td}}{2 \\cdot ${result.equivalentSectionResponse.areae} \\cdot ${roundToDecimals(result.fyd, 2)}}`}
                />
                <BlockMath math={`\\boxed{A_{sw}=${result.asw} \\, \\text{cm}^2/\\text{m}}`} />
                {result.ratio <= 0.67 && (
                  <>
                    <BlockMath math={`\\dfrac{V_{Sd}}{V_{Rd2}}+\\dfrac{T_{Sd}}{T_{Rd2}} \\leq 0,67`} />
                    <BlockMath math={`s_{máx} =0,6 \\cdot d \\leq 30 \\, \\text{cm}`} />
                    <BlockMath math={`s_{máx} =${roundToDecimals(result.aPart, 2)} \\leq 30 \\, \\text{cm}`} />
                    <BlockMath math={`s_{máx} =${result.maxSpacing} \\, \\text{cm}`} />
                  </>
                )}
                {result.ratio > 0.67 && (
                  <>
                    <BlockMath math={`\\dfrac{V_{Sd}}{V_{Rd2}}+\\dfrac{T_{Sd}}{T_{Rd2}} > 0,67`} />
                    <BlockMath math={`s_{máx} =0,3 \\cdot d \\leq 30 \\, \\text{cm}`} />
                    <BlockMath math={`s_{máx} =${roundToDecimals(result.aPart, 2)} \\leq 30 \\, \\text{cm}`} />
                    <BlockMath math={`s_{máx} =${result.maxSpacing} \\, \\text{cm}`} />
                  </>
                )}
                <InputDiv>
                  <p>Seleção da bitola</p>
                  <Select
                    id="stirrupSelect"
                    value={selectedStirrup}
                    onChange={(event) => setSelectedStirrup(event.target.value)}
                  >
                    {result.aswRebars.map((rebar, index) => (
                      <option key={index} value={rebar.name}>
                        {rebar.name}
                      </option>
                    ))}
                  </Select>
                </InputDiv>
                <p>
                  Serão necessários estribos de {selectedStirrup}c/
                  {result.aswRebars.find((r) => r.name == selectedStirrup).spacing}.
                </p>
                <p style={{ color: 'red' }}>Não verificada a armadura de cisalhamento.</p>
              </MathDiv>
              <h3>Armadura longitudinal</h3>
              <MathDiv>
                <BlockMath math={`A_{sl}=\\dfrac{T_{Sd}\\cdot u_e}{2 \\cdot A_e \\cdot f_{yd}}`} />
                <BlockMath
                  math={`A_{sl}=\\dfrac{${data.td}\\cdot ${result.equivalentSectionResponse.ue} }{2 \\cdot ${result.equivalentSectionResponse.areae} \\cdot ${roundToDecimals(result.fyd, 2)} }`}
                />
                <BlockMath math={`\\boxed{A_{sl}=${result.asl} \\, \\text{cm}^2}`} />
                <p>Dividindo para as face inferior e superior:</p>
                <BlockMath math={`A_{sl,face}=${result.asl / 2} \\, \\text{cm}^2`} />
                <InputDiv>
                  <p>Seleção da bitola</p>
                  <Select
                    id="rebarSelect"
                    value={selectedRebar}
                    onChange={(event) => setSelectedRebar(event.target.value)}
                  >
                    {result.aslRebars.map((rebar, index) => (
                      <option key={index} value={rebar.name}>
                        {rebar.name}
                      </option>
                    ))}
                  </Select>
                </InputDiv>
                <p>
                  Serão necessários {result.aslRebars.find((r) => r.name == selectedRebar).quantity} barras de{' '}
                  {selectedRebar} em cada uma das faces.
                </p>
                <p style={{ color: 'red' }}>Não verificada a armadura de flexão.</p>
              </MathDiv>
            </Result>
          </>
        )}
      </Container>
    </PageContainer>
  )
}
